import { AfterContentInit, Component, Input, OnDestroy } from '@angular/core';
import { combineLatest, fromEvent, merge, Observable, Subject } from 'rxjs';
import { map, shareReplay, switchMap, take, takeUntil, tap } from 'rxjs/operators';

@Component({
  selector: 'studio-video-controls',
  templateUrl: './video-controls.component.html',
  styleUrls: ['./video-controls.component.scss'],
})
export class VideoControlsComponent implements AfterContentInit, OnDestroy {
  @Input() video: HTMLVideoElement;

  private destroyed$ = new Subject<void>();

  public ready$: Observable<boolean>;
  public currentTime$: Observable<number>;
  public duration$: Observable<number>;

  ngAfterContentInit() {
    const video = this.video;

    // emits true when the next seek is completed
    const seekCompleted$ = fromEvent<{ target: HTMLVideoElement }>(video, 'seeked').pipe(
      // only want the first emission
      take(1),
      map(() => true),
    );

    // ordinarily video duration is not populated accurately until the video is played (webm format is likely a factor)
    // to force the duration we seek past then end of the video, then reset to the beginning
    // adapted from https://stackoverflow.com/a/55117919/1188074
    //
    // emits true when the video is loaded and duration has been accurately determined
    this.ready$ = fromEvent<{ target: HTMLVideoElement }>(video, 'loadeddata').pipe(
      tap(() => (video.currentTime = Number.MAX_SAFE_INTEGER)),
      switchMap(() => seekCompleted$.pipe(tap(() => (video.currentTime = 0)))),
      map(() => true),
      // ensure multiple subscriptions don't cause multiple loadedata event handlers to be attached
      // and supply the previous value to new subscribers
      shareReplay(1),
    );

    // emits the current time, when it changes and only after the video is ready
    this.currentTime$ = combineLatest([
      this.ready$,
      fromEvent<{ target: HTMLVideoElement }>(video, 'timeupdate'),
    ]).pipe(
      map(([_, { target }]) => target.currentTime),
      shareReplay(1),
    );

    // emits the duration of the video, when it changes and only after the video is ready
    this.duration$ = combineLatest([
      this.ready$,
      fromEvent<{ target: HTMLVideoElement }>(video, 'durationchange'),
    ]).pipe(
      map(([_, { target }]) => target.duration),
      shareReplay(1),
    );

    // manually subscribe to ensure that all subscriptions are live, otherwise we are dependent
    // on at least one external subscription to each observable
    merge(this.ready$, this.currentTime$, this.duration$)
      .pipe(takeUntil(this.destroyed$))
      .subscribe();
  }

  ngOnDestroy() {
    this.destroyed$.next();
    this.destroyed$.complete();
  }
}
